import { io } from 'socket.io-client'
import { WS_PATH } from '@apiary/shared'
import { refreshAccessToken, tokenStore } from './api'
import type { SocketFactory, SocketLike } from './ws'

/**
 * Выбор транспорта для WS.
 *
 * Реальный сокет импортируется статически (socket.io-client — обычная прод-зависимость),
 * а mock-транспорт **подменяет** фабрику через `setSocketFactoryOverride`. Так модуль
 * `src/mocks/*` остаётся достижимым только из динамического импорта в `main.tsx`
 * и не попадает в прод-бандл.
 */

let override: SocketFactory | null = null

export function setSocketFactoryOverride(factory: SocketFactory | null): void {
  override = factory
}

/**
 * Токен для очередной попытки соединения.
 *
 * Ключевое слово — «очередной». Раньше `auth` был обычным объектом, то есть
 * токен фиксировался в момент создания сокета. Access-токен живёт минуты, а
 * socket.io переподключается сам сколько угодно долго — и после истечения
 * токена каждая попытка получала UNAUTHORIZED. В шапке навсегда зависало
 * «переподключаемся», лечилось только перезагрузкой страницы. Функция-`auth`
 * вызывается перед КАЖДОЙ попыткой, поэтому берём актуальный токен, а если его
 * нет — обновляем.
 */
async function currentToken(): Promise<string> {
  const token = tokenStore.get()
  if (token !== null && token !== '') return token
  try {
    return await refreshAccessToken()
  } catch {
    // Refresh не помог: пусть попытка уйдёт с пустым токеном и честно получит
    // UNAUTHORIZED — обработчик сессии уже уведёт на страницу входа.
    return ''
  }
}

const realFactory: SocketFactory = (token: string): SocketLike => {
  // Первый токен известен сразу — используем его, не дожидаясь стора.
  let first: string | null = token
  const socket = io({
    path: WS_PATH,
    transports: ['websocket'],
    autoConnect: false,
    withCredentials: true,
    // docs/06 §8: токен только в handshake `auth`, не в query-строке.
    auth: (cb: (data: Record<string, unknown>) => void) => {
      const initial = first
      first = null
      if (initial !== null && initial !== '' && initial === tokenStore.get()) {
        cb({ token: initial })
        return
      }
      void currentToken().then(fresh => cb({ token: fresh }))
    }
  })
  // Дженерики socket.io описывают конкретные события; нам нужен узкий контракт
  // `SocketLike` — приведение осознанное и единственное в проекте.
  return socket as unknown as SocketLike
}

export function createSocketFactory(): SocketFactory {
  return override ?? realFactory
}
